import React from 'react'
import { inject, observer } from 'mobx-react'
import { withTranslation } from 'react-i18next'
import Icon from 'components/common/Icon'
import { getIconElement } from './poiIconUtils'
import PoiTabsAndCounters from './PoiTabsAndCounters'
import PoiBucketLists from './PoiBucketLists'
import PoiSaveButtons from './PoiSaveButtons'

class PoiSearchResults extends React.Component {
  constructor (props) {
    super(props)
    this.searchResultsRef = React.createRef()
    this.menuPositions = {}
    this.buttonRefs = {}
    this.state = {
      menuStep: 'main'
    }
  }

  getMenuPosition = (buttonElement, menuId) => {
    if (!buttonElement || !this.searchResultsRef.current) return null
    const searchResults = this.searchResultsRef.current
    const searchResultsRect = searchResults.getBoundingClientRect()
    const buttonRect = buttonElement.getBoundingClientRect()
    // Calculate position relative to search-results container
    // Position menu below the button, aligned to the right edge of the button
    const top = buttonRect.bottom - searchResultsRect.top + 6 // 6px gap below button
    const right = searchResultsRect.right - buttonRect.right
    return { top, right }
  }

  updateMenuPosition = (menuId) => {
    if (!menuId || !this.searchResultsRef.current) return
    const buttonElement = document.querySelector(`.search-results__btn[data-menu-id="${menuId}"]`)
    if (buttonElement) {
      const position = this.getMenuPosition(buttonElement, menuId)
      if (position) {
        this.menuPositions[menuId] = position
        this.forceUpdate()
      }
    }
  }

  handleMenuClickOutside = (event) => {
    const { dataStore } = this.props
    const poi = dataStore && dataStore.poi
    const target = event.target

    if (!poi || poi.openMenuId == null || !target || !target.closest) return

    const clickedMenu = target.closest('.search-results__menu')
    const clickedMenuTrigger = target.closest('.search-results__btn--kebab')

    if (!clickedMenu && !clickedMenuTrigger) {
      poi.openMenuId = null
    }
  }

  componentDidMount () {
    document.addEventListener('mousedown', this.handleMenuClickOutside)
  }

  componentDidUpdate (prevProps) {
    const { dataStore } = this.props
    const poi = dataStore && dataStore.poi
    const openMenuId = poi && poi.openMenuId
    const prevPoi = prevProps.dataStore && prevProps.dataStore.poi
    const prevOpenMenuId = prevPoi && prevPoi.openMenuId
    // Update menu position when menu opens or when markers change
    if (openMenuId && openMenuId !== prevOpenMenuId) {
      // Use a small delay to ensure DOM has updated
      setTimeout(() => {
        this.updateMenuPosition(openMenuId)
      }, 10)
    }
  }

  componentWillUnmount () {
    document.removeEventListener('mousedown', this.handleMenuClickOutside)
  }

  render () {
    const { dataStore, uiStore, t } = this.props
    const poi = dataStore && dataStore.poi
    const allMarkers = (poi && poi.temporaryMarkers) || [] // Results list is always full

    const choose = (id, bucket) => { if (poi && poi.setPendingBucket) poi.setPendingBucket(id, bucket) }
    const add = (id) => { if (poi && poi.addPendingToBucket) poi.addPendingToBucket(id) }

    // If no markers in Results section, show empty state instead of everything
    if (allMarkers.length === 0) {
      return (
        <div className='poi__saved-pois-empty'>
          <p className='poi__saved-pois-empty-title'>{t('POI.NO_SAVED_POIS_TITLE')}</p>
          <p className='poi__saved-pois-empty-text'>{t('POI.NO_SAVED_POIS_TEXT')}</p>
        </div>
      )
    }

    return (
      <div ref={this.searchResultsRef} className='search-results'>
        <div className='search-results__title'>{t('POI.RESULTS_TITLE')}</div>
        <div className='search-results__card'>
          {allMarkers.map((m, i) => {
            const currentList = (poi && poi.bucketById && poi.bucketById.get && poi.bucketById.get(m.id))
            const baselineListRaw = (poi && poi.sharedBucketsById && poi.sharedBucketsById.get && poi.sharedBucketsById.get(m.id)) || []
            const removedList = poi && poi.removedFromBucketById && typeof poi.removedFromBucketById.get === 'function'
              ? (poi.removedFromBucketById.get(m.id) || [])
              : []
            // Baseline buckets that are still effectively active (not explicitly removed)
            const baselineList = Array.isArray(baselineListRaw)
              ? baselineListRaw.filter(b => removedList.indexOf(b) === -1)
              : []
            const list = currentList || baselineList || []
            const isSelected = poi.disabledRowsById.get(m.id) || (baselineList && baselineList.length > 0)
            const pending = (poi && poi.pendingBucketById && poi.pendingBucketById.get && poi.pendingBucketById.get(m.id)) || 'general'
            const disablePlus = isSelected || (Array.isArray(list) && list.indexOf(pending) !== -1)

            const handleRowMouseEnter = () => {
              if (!uiStore || m == null) return
              // For saved markers, POI uses id like "saved_123" and savedMarkerId: 123,
              // but the actual map marker uses numeric id (123). Normalize so hover
              // and bounce target the real map marker.
              const idStr = String(m.id || '')
              const hoverTargetId = m.savedMarkerId ||
                (idStr.startsWith('saved_') ? parseInt(idStr.replace('saved_', ''), 10) : m.id)

              if (hoverTargetId == null) return
              if (typeof uiStore.setHoveredMarkerForZIndex === 'function') {
                uiStore.setHoveredMarkerForZIndex(hoverTargetId)
              }
              if (typeof uiStore.setHoveredPoiMarkerForAnimation === 'function') {
                uiStore.setHoveredPoiMarkerForAnimation(hoverTargetId)
              }
            }

            const handleRowMouseLeave = () => {
              if (!uiStore || m == null) return
              const idStr = String(m.id || '')
              const hoverTargetId = m.savedMarkerId ||
                (idStr.startsWith('saved_') ? parseInt(idStr.replace('saved_', ''), 10) : m.id)

              if (hoverTargetId == null) return
              if (typeof uiStore.clearHoveredMarkerForZIndex === 'function') {
                uiStore.clearHoveredMarkerForZIndex(hoverTargetId)
              }
              if (typeof uiStore.clearHoveredPoiMarkerForAnimation === 'function') {
                uiStore.clearHoveredPoiMarkerForAnimation(hoverTargetId)
              }
            }

            const handleZoomToMarker = () => {
              if (!uiStore || !uiStore.map || !uiStore.map.leafletElement) return
              const pos = m.position && m.position.latLng
              if (!pos || pos.lat == null || pos.lng == null) return

              // Normalize marker id: POI results may use "saved_123" + savedMarkerId,
              // while the actual map marker uses numeric id (123).
              const idStr = String(m.id || '')
              const activeMarkerId = m.savedMarkerId ||
                (idStr.startsWith('saved_') ? parseInt(idStr.replace('saved_', ''), 10) : m.id)

              // Mark this as the active marker for z-index/tooltip purposes
              if (activeMarkerId != null) {
                if (typeof uiStore.setClickedMarker === 'function') {
                  uiStore.setClickedMarker(activeMarkerId)
                }
                if (typeof uiStore.setHoveredMarkerForZIndex === 'function') {
                  uiStore.setHoveredMarkerForZIndex(activeMarkerId)
                }
              }

              // Keep the selected shape's context while explicitly zooming
              // toward this result; do not fall back to a center-only update.
              uiStore.zoomToMarker(pos)
            }

            return (
              <div
                key={m.id || i}
                className={`search-results__row ${isSelected ? 'search-results__row--selected' : ''}`}
                onMouseEnter={handleRowMouseEnter}
                onMouseLeave={handleRowMouseLeave}
              >
                <div
                  className='search-results__left'
                  onClick={handleZoomToMarker}
                >
                  {getIconElement(m)}
                  <div className='search-results__name' title={m.name}>{m.name}</div>
                </div>

                {/* If already selected (in any category), do not show actions */}
                {!isSelected && (
                  <div className='search-results__btn-group'>
                    <button
                      className='search-results__btn'
                      title={disablePlus
                        ? t('POI.ALREADY_SELECTED')
                        : t('POI.ADD_TO_BUCKET_TITLE', {
                          bucket: pending === 'ally'
                            ? t('POI.BUCKET_ALLIES')
                            : (pending === 'competitor'
                                ? t('POI.BUCKET_COMPETITORS')
                                : t('POI.BUCKET_GENERAL'))
                        })}
                      disabled={disablePlus}
                      onClick={function () {
                        if (!disablePlus) {
                          add(m.id)
                          uiStore.addNotification({
                            text: t('POI.ADDED_TO_BUCKET', {
                              name: m.name,
                              bucket: pending === 'ally'
                                ? t('POI.BUCKET_ALLIES')
                                : (pending === 'competitor'
                                    ? t('POI.BUCKET_COMPETITORS')
                                    : t('POI.BUCKET_GENERAL'))
                            }),
                            isError: false,
                            icon: () => getIconElement(m),
                            category: 'Success'
                          })
                        }
                      }}
                    >＋
                    </button>

                    <div className='search-results__kebab-wrap'>
                      <button
                        ref={(el) => {
                          if (el && m.id) {
                            this.buttonRefs[m.id] = el
                          }
                        }}
                        className='search-results__btn search-results__btn--kebab'
                        data-menu-id={m.id}
                        title={t('POI.CHOOSE_CATEGORY')}
                        onClick={function (e) {
                          this.setState({ menuStep: 'main' })
                          if (poi && poi.toggleMenu && m.id != null) {
                            // Calculate position synchronously before toggling menu
                            const position = this.getMenuPosition(e.currentTarget, m.id)
                            if (position) {
                              this.menuPositions[m.id] = position
                            }
                            // Set source to distinguish from bucket list menu
                            poi.openMenuSource = 'results'
                            if (poi.menuStepById) poi.menuStepById.set(m.id, 'main')
                            // Toggle menu - this will trigger MobX re-render
                            poi.toggleMenu(m.id)
                          }
                        }.bind(this)}
                      >⋮
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Render menus outside the card, positioned absolutely */}
        {allMarkers.map((m) => {
          const isMenuOpen = poi && poi.openMenuId === m.id && poi.openMenuSource === 'results'
          if (!isMenuOpen) return null
          // Calculate position on render using stored button ref or fallback to stored position
          const buttonElement = this.buttonRefs[m.id] || document.querySelector(`.search-results__btn[data-menu-id="${m.id}"]`)
          const menuPosition = buttonElement ? this.getMenuPosition(buttonElement, m.id) : this.menuPositions[m.id]
          // If position calculation succeeded, store it
          if (menuPosition && buttonElement) {
            this.menuPositions[m.id] = menuPosition
          }
          // If still no position, try to update it asynchronously
          if (!menuPosition) {
            setTimeout(() => {
              const btn = this.buttonRefs[m.id] || document.querySelector(`.search-results__btn[data-menu-id="${m.id}"]`)
              if (btn) {
                const pos = this.getMenuPosition(btn, m.id)
                if (pos) {
                  this.menuPositions[m.id] = pos
                  this.forceUpdate()
                }
              }
            }, 10)
            return null // Don't render until we have a position
          }
          return (
            <div
              key={`menu-${m.id}`}
              className='search-results__menu'
              style={{
                position: 'absolute',
                top: `${menuPosition.top}px`,
                right: `${menuPosition.right}px`,
                zIndex: 1000
              }}
            >
              {this.state.menuStep === 'categories' && (
                <button
                  className='search-results__menu-back'
                  style={{
                    position: 'absolute',
                    left: '8px',
                    top: '8px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#8C94A0'
                  }}
                  onClick={() => this.setState({ menuStep: 'main' })}
                >
                  ‹
                </button>
              )}
              <button
                className='search-results__menu-close'
                onClick={function () {
                  if (poi && poi.toggleMenu && m.id != null) {
                    poi.toggleMenu(m.id)
                  }
                }}
                type='button'
              >
                {t('GLOBAL.CLOSE')}
              </button>
              {this.state.menuStep === 'main'
                ? (
                  <button
                    className='search-results__mi'
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      paddingBottom: '8px',
                      paddingTop: '8px',
                      marginTop: '20px'
                    }}
                    onClick={() => this.setState({ menuStep: 'categories' })}
                  >
                    <span>Move</span>
                    <Icon id='fa-caret-right' />
                  </button>
                  )
                : (
                  <div className='search-results__menu-options'>
                    {[
                      { value: 'general', label: t('POI.BUCKET_GENERAL') },
                      { value: 'competitor', label: t('POI.BUCKET_COMPETITORS') },
                      { value: 'ally', label: t('POI.BUCKET_ALLIES') }
                    ].map(option => (
                      <button
                        key={option.value}
                        className='search-results__menu-option-btn'
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '8px 12px',
                          fontSize: '14px',
                          color: '#292C44',
                          display: 'block'
                        }}
                        onClick={() => {
                          choose(m.id, option.value)
                          add(m.id)
                          uiStore.addNotification({
                            text: t('POI.ADDED_TO_BUCKET', {
                              name: m.name,
                              bucket: option.label
                            }),
                            category: 'Success'
                          })
                          if (poi) poi.openMenuId = null
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  )}
            </div>
          )
        })}

        <PoiTabsAndCounters />

        <PoiBucketLists />

        <PoiSaveButtons />
      </div>
    )
  }
}

export default withTranslation()(
  inject('dataStore', 'uiStore', 'selectionStore')(observer(PoiSearchResults))
)
